import React from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { NotificationManager } from "react-notifications";

export default function withAuthProvider(WrappedComponent) {
  return class extends React.Component {
    static publicClientApplication = {};

    constructor(props) {
      super(props);
      this.state = {
        error: null,
        isAuthenticated: false,
        user: {},
      };

      // Initialize the MSAL application object
      this.publicClientApplication = new PublicClientApplication({
        auth: {
          clientId: process.env.APP_ID || ".env not file",
          redirectUri: process.env.REDIRECT_URI || ".env not file",
          authority: process.env.TENANT || ".env not file",
        },
        cache: {
          cacheLocation: "sessionStorage",
          storeAuthStateInCookie: false,
        },
      });
    }

    componentDidMount() {
      // If MSAL already has an account, the user
      // is already logged in

      const accounts = this.publicClientApplication.getAllAccounts();

      if (accounts && accounts.length > 0) {
        // Enhance user object with data from Graph
        this.getUserProfile();
      }
    }

    render() {
      return (
        <WrappedComponent
          error={this.state.error}
          isAuthenticated={this.state.isAuthenticated}
          user={this.state.user}
          handleLogin={() => this.handleLogin()}
          handleLogOut={() => this.handleLogOut()}
          getAccessToken={(scopes) => this.getAccessToken(scopes)}
          setError={(message, debug) => this.setErrorMessage(message, debug)}
          {...this.props}
        />
      );
    }

    async handleLogin() {
      try {
        // Login via popup
        await this.publicClientApplication.loginPopup({
          scopes: JSON.parse(process.env.SCOPES),
          prompt: "select_account",
        });

        // After login, get the user's profile
        await this.getUserProfile();
      } catch (error) {
        const errorMessage = error.message.split("\n")[0];
        NotificationManager.error(errorMessage, "", 5000);
        this.setState({
          isAuthenticated: false,
          user: {},
          error: this.normalizeError(error),
        });
      }
    }

    handleLogOut() {
      this.setState({
        error: null,
        isAuthenticated: false,
        user: {},
      });
      window.sessionStorage.clear();
    }

    async getAccessToken(scopes) {
      try {
        const accounts = this.publicClientApplication.getAllAccounts();

        if (accounts.length <= 0) throw new Error("login_required");
        // Get the access token silently
        // If the cache contains a non-expired token, this function
        // will just return the cached token. Otherwise, it will
        // make a request to the Azure OAuth endpoint to get a token
        let silentResult =
          await this.publicClientApplication.acquireTokenSilent({
            scopes: scopes,
            account: accounts[0],
          });

        return silentResult.accessToken;
      } catch (error) {
        const errorMessage = error.message.split("\n")[0];
        NotificationManager.error(errorMessage, "", 5000);
        // If a silent request fails, it may be because the user needs
        // to login or grant consent to one or more of the requested scopes
        if (this.isInteractionRequired(error)) {
          let interactiveResult =
            await this.publicClientApplication.acquireTokenPopup({
              scopes: scopes,
            });

          return interactiveResult.accessToken;
        } else {
          throw error;
        }
      }
    }

    async getUserProfile() {
      try {
        let accessToken = await this.getAccessToken(
          JSON.parse(process.env.SCOPES)
        );

        if (accessToken) {
          // Get the user's profile from Graph
          this.setState({
            isAuthenticated: true,
            user: { accessToken: accessToken },
            error: null,
          });
        }
      } catch (error) {
        const errorMessage = error.message.split("\n")[0];
        NotificationManager.error(errorMessage, "", 5000);
        this.setState({
          isAuthenticated: false,
          user: {},
          error: this.normalizeError(error),
        });
      }
    }

    setErrorMessage(message, debug) {
      this.setState({
        error: { message: message, debug: debug },
      });
    }

    normalizeError(error) {
      let normalizedError = {};
      if (typeof error === "string") {
        let errParts = error.split("|");
        normalizedError =
          errParts.length > 1
            ? { message: errParts[1], debug: errParts[0] }
            : { message: error };
      } else {
        normalizedError = {
          message: error.message,
          debug: JSON.stringify(error),
        };
      }
      return normalizedError;
    }

    isInteractionRequired(error) {
      if (!error.message || error.message.length <= 0) {
        return false;
      }

      return (
        error.message.indexOf("consent_required") > -1 ||
        error.message.indexOf("interaction_required") > -1 ||
        error.message.indexOf("login_required") > -1 ||
        error.message.indexOf("no_account_in_silent_request") > -1
      );
    }
  };
}
